import React from 'react';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { ICampaignOverview } from '../../../../../model/briefing/overview/ICampaignOverview';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { formatDate, truncateString } from '../../../../../shared/functions/Functions';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { UserRoles } from '../../../../../enum/UserRoles';
import { gtmService } from '../../../../../utils/gtm.service';
import { ProgressBar } from 'react-bootstrap';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import PaymentInfoBlock from '../../../../../shared/components/PaymentInfoBlock';
import { PaymentTermsEnum } from '../../../../../enum/PaymenTermsEnum';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { ContractAmendmentIcon } from './ContractAmendmentIcon';

interface IContractListItemProps {
    campaign?: ICampaignOverview;
}

const ContractListItem = ({ campaign }: IContractListItemProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        user: { userType },
    } = useSelector((reduxState: IAllStates) => reduxState.auth);

    const isAgency = [UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles);

    const navigateToCampaignOverview = (campaignId: number) => {
        isAgency
            ? navigate(`/agency/campaign/${campaignId}/overview`)
            : navigate(`/influencer/campaign/${campaignId}/overview`);
        gtmService('select_content');
    };
    const truncatedTitle = truncateString(campaign?.title ?? '', 40);
    const isTruncated = (campaign?.title?.length ?? 0) > 40;
    return (
        <CardLayout classBody="contract-list-card px-3 py-0">
            <div className="image-wrapper">
                <CustomImageHandler
                    thumbnailPath={campaign?.campaignImages?.[0]?.thumbnail}
                    photoPath={campaign?.campaignImages?.[0]?.thumbnail}
                    altTag="Campaign Image"
                />
                <span className={`status-badge ${campaign?.isLongTerm ? 'long' : 'linked'}`}>
                    {t(`brand.campaign.briefing.overview_card.${campaign?.isLongTerm ? 'long_term' : 'linked'}`)}
                </span>
            </div>
            <div className="card-content pl-3">
                <div className="d-flex align-items-center">
                    <TooltipOverlay content={isTruncated ? campaign?.title : ''}>
                        <h5
                            className="mb-0 cursor-pointer"
                            onClick={() => navigateToCampaignOverview(campaign?.id ?? 0)}
                        >
                            {truncatedTitle}
                        </h5>
                    </TooltipOverlay>
                    <ContractAmendmentIcon contract={campaign?.contracts?.[0]} />
                </div>
                <p className="mb-0 text-primary">{campaign?.company?.displayName ?? '-'}</p>
            </div>

            <div className="card-info">
                <div className="info-block">
                    <div className="info-main">
                        {campaign?.date ? formatDate(campaign?.date as string) : 'DD.MM.YYYY.'}
                    </div>
                    <div>{t('brand.campaign.briefing.step3.start_date')}</div>
                </div>
                <div className="info-block">
                    <div className="info-main">
                        {!!campaign?.invoiceDuration
                            ? t(`brand.campaign.briefing.step3.campaign_duration_enum.${campaign?.invoiceDuration}`)
                            : '-'}
                    </div>
                    <div>{t('brand.campaign.briefing.step3.campaign_duration')}</div>
                </div>
                <div className="info-block">
                    <PaymentInfoBlock
                        main={t(
                            `brand.campaign.briefing.step8.payment_terms_type.${
                                campaign?.paymentTerms === PaymentTermsEnum.OTHER ? 'custom' : campaign?.paymentTerms
                            }`
                        )}
                        label={t('brand.campaign.briefing.step8.payment_terms')}
                        showTooltip={campaign?.paymentTerms === PaymentTermsEnum.OTHER}
                        tooltipContent={campaign?.paymentTermsCustom ?? ''}
                        tooltipId="payment_tooltip"
                        mainClassName="info-main"
                    />
                </div>
                <div className="info-block">
                    <div
                        className={`info-main ${
                            !campaign?.isCharity && 'euro-symbol'
                        } text-primary font-weight-extra-bold`}
                    >
                        {campaign?.isCharity
                            ? t('brand.account.price_list_modal.barter_deals')
                            : priceSplitter(Math.round(campaign?.contracts?.[0]?.price ?? 0))}
                    </div>
                    <div>{!campaign?.isCharity && t('brand.campaign.book.offer.modal.price')}</div>
                </div>
                <div className="info-block">
                    <div className="d-flex justify-content-between mb-1">
                        <p className="mb-0">
                            Postings completed
                            <span className="text-primary ml-3 font-weight-extra-bold">
                                {campaign?.contracts?.[0]?.postingsUsed ?? 0}
                            </span>
                            /<span>{campaign?.contracts?.[0]?.nbPosts ?? 0}</span>
                        </p>
                        <p className="mb-0">{campaign?.contracts?.[0]?.usagePercentage ?? 0}%</p>
                    </div>
                    <ProgressBar
                        variant="primary"
                        className="progress-sm w-100 bg-primary-lighter"
                        now={campaign?.contracts?.[0]?.usagePercentage}
                    />
                </div>
            </div>
        </CardLayout>
    );
};

export default React.memo(ContractListItem);
